import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import moment from 'moment';
import {
  Button,
  Checkbox,
  CheckboxGroup,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  FormText,
  Input,
  Label,
  Mask,
  RadioGroup,
  Row,
  Select,
  Show,
} from '../../components';
import { Field, useFormikContext } from 'formik';
import axios from 'axios';
import { Modal, ModalBody } from 'reactstrap';
import { useSelector } from 'react-redux';
import { NoteLabel } from '../../components/NoteLabel';
import SelectMut from '../../components/MutiSelect/SelectMuti';
import SelectSegCus from '../../components/SelectSegCus';

const isCIFRequired = ({ bank, value }) => bank === 'NAB' && !!value && 'KH_VANG_LAI' !== value && 'KH_OTH' !== value;

const SubmitWithCheckBtn = () => {
  const [open, setOpen] = useState(false);
  const { values, submitForm, isSubmitting, isValid } = useFormikContext();
  return (
    <>
      <button
        type='button'
        className='btn btn-primary'
        disabled={!isValid}
        onClick={async () => {
          const response = await axios.post('/api/leads?check', { ...values, dob: values.dob + ' 07:00:00' });
          const isDuplicated = response.data.isDuplicated;
          if (isDuplicated) {
            setOpen(true);
          } else {
            submitForm();
          }
        }}
      >
        Gửi thông tin
      </button>
      <Modal isOpen={open} size='lg' centered fade>
        <ModalBody>
          <div className='pt-4 px-5 pb-2'>
            Khách hàng có thông tin tương tự đã được tạo trong vòng 15 ngày gần đây, có thể dễ dàng tìm lại. Anh chị vui
            lòng cân nhắc việc tiếp tục vì:
            <ul>
              <li>Tạo trùng KH khiến việc quản lý DSKH khó hơn</li>
              <li>Tạo trùng KH khiến tỉ lệ chuyển đổi thấp hơn thực tế</li>
            </ul>
          </div>
          <div className='d-flex flex-column flex-lg-row pt-3 justify-content-between'>
            <button className='btn btn-primary' onClick={() => setOpen(false)} disabled={isSubmitting}>
              Quay lại
            </button>
            <button
              className='btn btn-secondary'
              style={{ color: '#fff', backgroundColor: '#6c757d', borderColor: '#6c757d' }}
              disabled={isSubmitting}
              onClick={submitForm}
            >
              Tiếp tục tạo Khách hàng
            </button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};

export const LeadForm = ({ values, onSubmit, bo, to, fsc, options, version, roles, new: isNew }) => {
  let { ROLE_BANK_STAFF, ROLE_FSC, ROLE_EDITOR, IRIS } = roles || {};
  const [hrmUpdateDeadline, setHrmUpdateDeadline] = useState(moment().add(1, 'M'));
  const [hrmUpdateDeadlineExtra, setHrmUpdateDeadlineExtra] = useState(moment().add(1, 'M'));
  const [now] = useState(moment());
  const isHrmEditable =
    isNew ||
    (ROLE_EDITOR && ROLE_BANK_STAFF) ||
    (ROLE_BANK_STAFF && hrmUpdateDeadline.isValid() && hrmUpdateDeadline.isAfter(now));
  const isHrmEditableExtra30days =
    isNew ||
    (ROLE_EDITOR && ROLE_BANK_STAFF) ||
    (ROLE_BANK_STAFF && hrmUpdateDeadlineExtra.isValid() && hrmUpdateDeadlineExtra.isAfter(now));
  const bank = useSelector((state) => state?.lookup?.bank);

  useEffect(() => {
    const isHavingPolicy = !!values?.poNo;
    const isBankStaffUpdated =
      values?.flag === null || // old cases
      values?.refNo?.startsWith('BCA') || // Lead create from LTS
      values?.flag === 'UPDATED'; // Lead create from IRIS
    if (isHavingPolicy && isBankStaffUpdated) {
      const hrmUpdateDeadLine = moment(values?.firstIssueDate, 'DD/MM/YYYY');
      if (hrmUpdateDeadLine.isValid()) {
        setHrmUpdateDeadline(hrmUpdateDeadLine.add(1, 'M').set('date', 5).endOf('day'));
        setHrmUpdateDeadlineExtra(hrmUpdateDeadLine.add(1, 'M').endOf('day'));
      }
    }
  }, [values]);

  let hrmOptions = useSelector((state) => {
    let options = [
      { value: '', label: 'Chọn...' },
      ...(state?.lookup?.managers || []).map((e) => ({
        value: e.businessCd,
        label: `${e.agentName} (${e.businessCd})`,
      })),
    ];
    if (!!values.leaderCode && options.filter((o) => o.value === values.leaderCode)?.length === 0)
      options = [
        ...options,
        {
          value: values.leaderCode,
          label: values.leaderCode,
        },
      ];

    return options;
  });

  let y = {};
  if (ROLE_BANK_STAFF) {
    y.productCares = Yup.string().required('Chọn sản phẩm quan tâm từ danh sách').nullable();
    y.ownedContract = Yup.string().required('Chọn trả lời câu hỏi trên').nullable();
    y.inMonthLoan = Yup.string().required('Vui lòng lựa chọn Có/Không').nullable();
    y.segmentLists = Yup.string().required('Chọn phân khúc khách hàng từ danh sách').nullable();
    y.fscCode = Yup.string().required('Chọn FSC từ danh sách').nullable();
    y.customerSource = Yup.string().required('Chọn từ danh sách').nullable();
    y.customerEvent = Yup.string().required('Chọn từ danh sách').nullable();
    y.customerRelationship = Yup.string().required('Chọn từ danh sách').nullable();
    y.title = Yup.string().required('Chọn từ danh sách').nullable();
    y.fullName = Yup.string().required('Nhập nội dung').nullable();
    y.dob = Yup.string()
      .nullable()
      .required('Nhập thông tin')
      .matches(/^[0-9]{2}\/[0-9]{2}\/[0-9]{4}( [0-9]{2}:[0-9]{2}:[0-9]{2,6})?$/, 'Nhập giá trị theo định dạng DD/MM/YYYY, vd: 31/12/2019')
      .test({
        name: 'is-date',
        message: 'Ngày tháng năm sinh không hợp lệ',
        test: (value) => {
          let result = true;
          let dob = moment(value, 'DD/MM/YYYY');
          if (!dob.isValid()) result = false;
          else {
            let now = moment();
            if (dob.isAfter(now)) result = false;
            else {
              let age = now.diff(dob, 'year');
              if (age < 0 || age > 150) result = false;
            }
          }
          return result;
        },
      });
    y.mobileNumber = Yup.string()
      .nullable()
      .required('Số điện thoại từ 10 số, bắt đầu bằng số 0')
      .matches(/^(?=\d{10}$)(0)\d+/, 'Số điện thoại từ 10 số, bắt đầu bằng số 0')
      .nullable();
    y.allowedToContact = Yup.mixed().test({
      name: 'not-empty',
      test: (value) => !!value,
    });
    y.isConsult = Yup.mixed().test({
      name: 'not-empty',
      test: (value) => !!value,
    });
    y.isCommit = Yup.mixed().test({
      name: 'not-empty',
      test: (value) => !!value,
    });
    y.idType = Yup.string().nullable().required();
    y.idNumber = Yup.string()
      .nullable()
      .required('Nhập số Căn cước/CMND/CCCD/PP')
      .when(['idType'], {
        is: (idType) => 'NI' === idType,
        then: Yup.string()
          .nullable()
          .matches(/^\d{12}$|^\d{9}$/, 'Căn cước, CCCD có 12 số; CMND có 9 số hoặc 12 số'),
      })
      .when(['idType'], {
        is: (idType) => 'PP' === idType,
        then: Yup.string()
          .nullable()
          .matches(/^[A-Za-z0-9]{8,9}$/, 'Bắt đầu bằng kí tự và có 7 hoặc 8 số'),
      });

    if (version === 4) {
      y.leaderCode = Yup.string().nullable().required('Chọn Mã HRM của lãnh đạo phòng');
      y.positionNote = Yup.string()
        .nullable()
        .when(['position'], {
          is: (value) => 'OTHER' === value,
          then: Yup.string().nullable().required('Vui lòng nhập thông tin...'),
        });
      y.occupationNote = Yup.string()
        .nullable()
        .when(['occupation'], {
          is: (value) => 'OTHER' === value,
          then: Yup.string().required('Vui lòng nhập thông tin nghành nghề...').nullable(),
        });
    } else {
      y.approach = Yup.string().nullable().required('Vui lòng nhập thông tin...');
      y.approachNote = Yup.string()
        .nullable()
        .when(['approach'], {
          is: (value) => 'OTHER' === value,
          then: Yup.string().nullable().required('Vui lòng nhập thông tin...'),
        });

      y.bankerApproach = Yup.string().nullable().required('Vui lòng nhập thông tin...');
      y.bankerApproachNote = Yup.string()
        .nullable()
        .when(['bankerApproach'], {
          is: (value) => 'OTHER' === value,
          then: Yup.string().nullable().required('Vui lòng nhập thông tin...'),
        });
    }

    y.email = Yup.string().nullable().email('Email không hợp lệ');

    y.cif = Yup.string()
      .nullable()
      .when(['customerSource'], {
        is: (value) => isCIFRequired({ bank, value }),
        then: Yup.string().nullable().required('Vui lòng nhập thông tin...'),
      });
  } else if (ROLE_FSC) {
    //y.lov = Yup.string().nullable().nullable().required('Nhập Định danh Lead');
    //y.estimatedFyp = Yup.string().nullable().nullable().required('FYP ước tính');
    // y.poNo = Yup.string().nullable().nullable().required('Nhập Số hợp đồng');
  }

  let validationSchema = Yup.object(y);

  const introducerHrm = () => {
    return (
      <Col md='6'>
        <FormGroup>
          <Label for='introducerHrm'>Mã người giới thiệu {version === 4 && '(theo mã HRM)'}</Label>
          <Input name='introducerHrm' readOnly={!isHrmEditable} />
          <FormFeedback name='introducerHrm' />
        </FormGroup>
      </Col>
    );
  };
  const priorityVCV = () => {
    return (
      <Show condition={version === 4}>
        <Col md='6'>
          <FormGroup row>
            <Label for='priority' md='4'>
              {bank === 'VCB' ? 'KH Priority của VCB' : 'KH Ưu tiên'}
            </Label>
          </FormGroup>
          <FormGroup row>
            <Col md={8}>
              <RadioGroup
                name='priority'
                options={options?.priority}
                inline
                readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)}
              />
            </Col>
          </FormGroup>
        </Col>
      </Show>
    );
  };

  const ex = () => {
    return (
      <Show if={bank !== 'VCB' && bank !== 'AGR' && bank !== 'AGB'}>
        <Col md='6'>
          <FormGroup>
            <Label for='occupation'>Chức danh</Label>
            <Select name='occupation' options={options?.position}
                    readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)} />
            <Field name='occupation'>
              {({ field }) =>
                field.value === 'OTHER' ? (
                  <div className='pt-2'>
                    <Input type='textarea' name='occupationNote' readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)} />
                    <FormFeedback name='occupationNote' />
                  </div>
                ) : null
              }
            </Field>
          </FormGroup>
        </Col>
        <Col md='6'>
          <FormGroup>
            <Label for='financeBg'>KH có chuyên môn/kinh nghiệm làm việc trong lĩnh vực tài chính không?</Label>
            <div>
              <RadioGroup
                name='financeBg'
                options={options?.financeBg}
                inline
                readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)}
              />
            </div>
          </FormGroup>
        </Col>
      </Show>
    );
  };

  return (
    <div>
      <Form values={values} onSubmit={onSubmit} validationSchema={validationSchema}>
        <fieldset>
          <legend>Phần thông tin nội bộ</legend>
          <Row>
            <Col md='6'>
              <FormGroup>
                <Label for='fscCode' required>
                  Tên FSC
                </Label>
                <Show condition={ROLE_BANK_STAFF}>
                  <>
                    <Select
                      name='fscCode'
                      options={fsc?.allOptions}
                      readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR && isNew)}
                    />
                    <FormFeedback name='fscCode' />
                  </>
                </Show>
                <Show condition={!ROLE_BANK_STAFF}>
                  <Input name='fscName' plaintext />
                </Show>
              </FormGroup>
            </Col>
            <Col md='6'>
              <FormGroup>
                <Label>Mã FSC</Label>
                <Input name='fscCode' plaintext />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md='6'>
              <FormGroup>
                <Label>Chi nhánh</Label>
                <div className='input-group'>
                  <div className='form-control'>{bo?.name}</div>
                  <div className='input-group-append'>
                    <span className='input-group-text'>Mã: {bo?.bizCode}</span>
                  </div>
                </div>
              </FormGroup>
            </Col>
            <Col md='6'>
              <FormGroup>
                <Label>Phòng</Label>
                <div className='input-group'>
                  <div className='form-control'>{to?.name}</div>
                  <div className='input-group-append'>
                    <span className='input-group-text'>Mã: {to?.bizCode}</span>
                  </div>
                </div>
              </FormGroup>
            </Col>
          </Row>
          <Show condition={!ROLE_BANK_STAFF}>
            <Row>
              <Col md='6'>
                <FormGroup>
                  <Label>Tên nhân viên ngân hàng</Label>
                  <Input name='bankStaffName' plaintext />
                </FormGroup>
              </Col>
              <Col md='6'>
                <FormGroup>
                  <Label>Mã viên ngân hàng</Label>
                  <Input name='bankStaffCode' plaintext />
                </FormGroup>
              </Col>
            </Row>
          </Show>
          <Row>
            <Col md='6'>
              <FormGroup>
                <Label for='customerRelationship' required>
                  Mối quan hệ của CBNH
                </Label>
                <Select
                  name='customerRelationship'
                  options={options?.customerRelationship}
                  readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)}
                />
                <FormFeedback name='customerRelationship' />
              </FormGroup>
            </Col>
            <Show condition={version === 2}>
              {introducerHrm()}
            </Show>
          </Row>
          <Row>
            <Show condition={version === 4}>
              {introducerHrm()}
              <Col md='6'>
                <FormGroup>
                  <Label for='leaderCode' required>
                    Mã lãnh đạo phòng (mã HRM)
                  </Label>
                  <Select name='leaderCode' options={hrmOptions} readOnly={!isHrmEditable} />
                  <FormText color='secondary'>Mã HRM của Lãnh đạo Phòng để phục vụ việc tính toán incentives</FormText>
                  <FormFeedback name='leaderCode' />
                </FormGroup>
              </Col>
            </Show>
          </Row>
        </fieldset>
        <fieldset>
          <legend>Phần thông tin khách hàng</legend>
          <Row>
            <Col md='6'>
              <FormGroup>
                <Label for='title' required>
                  Danh xưng
                </Label>
                <Select name='title' options={options?.title} readOnly={IRIS || !(ROLE_BANK_STAFF && ROLE_EDITOR)} />
                <FormFeedback name='title' />
              </FormGroup>
            </Col>
            <Col md='6'>
              <FormGroup>
                <Label for='fullName' required>
                  Họ và tên
                </Label>
                <Input name='fullName' readOnly={IRIS || !(ROLE_BANK_STAFF && ROLE_EDITOR)} />
                <FormFeedback name='fullName' />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md='6'>
              <FormGroup>
                <Label for='dob' required>
                  Ngày tháng năm sinh
                </Label>
                <Mask
                  name='dob'
                  mask='99/99/9999'
                  placeholder='DD/MM/YYYY'
                  readOnly={IRIS || !(ROLE_BANK_STAFF && ROLE_EDITOR)}
                />
                <FormFeedback name='dob' />
              </FormGroup>
            </Col>
            <Col md={version === 4 ? '6' : '3'}>
              <FormGroup>
                <Label for='maritalStatus'>Tình trạng hôn nhân</Label>
                <Select name='maritalStatus' options={options?.marital} readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)} />
                <FormFeedback name='maritalStatus' />
              </FormGroup>
            </Col>
            <Show condition={version === 2}>
              <Col md='3'>
                <FormGroup>
                  <Label for='children'>Số con</Label>
                  <Input name='children' type='number' readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)} />
                  <FormFeedback name='children' />
                </FormGroup>
              </Col>
            </Show>
          </Row>
          <Row>
            <Col md='6'>
              <FormGroup>
                <Label for='mobileNumber' required>
                  Số điện thoại liên lạc
                </Label>
                <Input name='mobileNumber' readOnly={IRIS || !(ROLE_BANK_STAFF && ROLE_EDITOR)} />
                <FormFeedback name='mobileNumber' />
              </FormGroup>
            </Col>
            <Col md='6'>
              <FormGroup>
                <Label for='email'>Địa chỉ email</Label>
                <Input name='email' readOnly={IRIS || !(ROLE_BANK_STAFF && ROLE_EDITOR)} />
                <FormFeedback name='email' />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md={`6`}>
              <FormGroup>
                <RadioGroup
                  name='idType'
                  options={options?.idType}
                  inline
                  readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)}
                />
                <FormFeedback name='idType' readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)} />
              </FormGroup>
              <FormGroup row>
                <Label for='idNumber' md={2} required>
                  Số
                </Label>
                <Col md={10}>
                  <Input name='idNumber' readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)} />
                  <FormFeedback name='idNumber' />
                </Col>
              </FormGroup>
            </Col>
            <Show condition={version === 2}>
              <Col md='6'>
                <FormGroup>
                  <Label for='interest' style={{ marginBottom: '1rem' }}>Số tài khoản thanh toán khách hàng</Label>
                  <div>
                    <Input
                      name='bankAccountNumber'
                      readOnly={!isHrmEditable}
                    />
                  </div>
                </FormGroup>
              </Col>
            </Show>
            <Show condition={version === 2}>
              <Col md='6'>
                <FormGroup>
                  <Label for='approach' required>
                    Cách thức tiếp cận
                  </Label>
                  <RadioGroup name='approach' options={options?.approach}
                              readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)} />
                  <Field name='approach'>
                    {({ field, form }) => (
                      <Show condition={field.value === 'OTHER'}>
                        <div className='pt-2'>
                          <Input
                            type='textarea'
                            name='approachNote'
                            readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)}
                            onUnmount={() => form.setFieldValue('approachNote', '')}
                          />
                          <FormFeedback name='approachNote' />
                        </div>
                      </Show>
                    )}
                  </Field>
                  <FormFeedback name='approach' />
                </FormGroup>
              </Col>
              <Col md='6'>
                <FormGroup>
                  <Label for='approach' required>
                    Khách hàng đồng ý cho FSC tư vấn sản phẩm bảo hiểm
                  </Label>
                  <RadioGroup
                    name='bankerApproach'
                    options={[
                      { value: 'CALL', label: 'Gọi điện thoại' },
                      { value: 'FACE2FACE', label: 'Gặp trực tiếp' },
                      { value: 'EMAIL', label: 'Gửi email' },
                      { value: 'OTHER', label: 'Khác' },
                    ]}
                    readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)}
                  />
                  <Field name='bankerApproach'>
                    {({ field, form }) => (
                      <Show condition={field.value === 'OTHER'}>
                        <div className='pt-2'>
                          <Input
                            type='textarea'
                            name='bankerApproachNote'
                            readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)}
                            onUnmount={() => form.setFieldValue('bankerApproachNote', null)}
                          />
                          <FormFeedback name='bankerApproachNote' />
                        </div>
                      </Show>
                    )}
                  </Field>
                  <FormFeedback name='approach' />
                </FormGroup>
              </Col>
            </Show>
          </Row>
          <Show condition={version === 2 || version === 4}>
            <Row>
              <Show if={bank === 'VCB'}>
                <Col md='6'>
                  <FormGroup>
                    <Label for='position'>Nghề nghiệp</Label>
                    <Select name='position' options={options?.occupation}
                            readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)} />
                    <Field name='position'>
                      {({ field }) =>
                        field.value === 'OTHER' ? (
                          <div className='pt-2'>
                            <Input type='textarea' name='positionNote' readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)} />
                            <FormFeedback name='positionNote' />
                          </div>
                        ) : null
                      }
                    </Field>
                  </FormGroup>
                </Col>
              </Show>
              <Show if={bank === 'VCB'}>
                <Col md='6'>
                  <FormGroup>
                    <Label for='income'>Thu nhập hàng tháng ước tính (triệu VNĐ)</Label>
                    <Select name='income' options={options?.income} readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)} />
                  </FormGroup>
                </Col>
              </Show>
              <Col md='6'>
                <FormGroup>
                  <Label for='segmentLists' required>Phân khúc khách hàng </Label>
                  <Select
                    name='segmentLists'
                    options={options?.segmentLists}
                    readOnly={!isHrmEditableExtra30days}
                  />
                  <FormFeedback name='segmentLists' />
                </FormGroup>
              </Col>
              <Show if={bank === 'NAB'}>
                <Field name='customerSource'>
                  {({ field }) => (
                    <Col md='6'>
                      <FormGroup>
                        <Label for='cif' required={isCIFRequired({ bank, value: field.value })}>
                          Số CIF
                        </Label>
                        <Input name='cif' readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)} />
                        <FormFeedback name='cif' />
                      </FormGroup>
                    </Col>
                  )}
                </Field>
              </Show>
              <Col md='6'>
                <FormGroup>
                  <Label for='customerEvent' required>
                    Khách hàng từ sự kiện bán hàng?
                  </Label>
                  <Select
                    name='customerEvent'
                    options={options?.customerEvent}
                    readOnly={!isHrmEditableExtra30days}
                  />
                  <FormFeedback name='customerEvent' />
                </FormGroup>
              </Col>
              <Col md='12'>
                <FormGroup>
                  <NoteLabel for='customerSource' required note={'Có thể chọn nhiều hơn 1 nhóm sản phẩm'}>
                    Sản phẩm Ngân hàng khách hàng đang sử dụng
                  </NoteLabel>
                  <Row>
                    <Col md='4'>
                      <FormGroup>
                        <SelectSegCus
                          name='customerSource'
                          options={options?.customerSource}
                          readOnly={!isHrmEditableExtra30days}
                          type={1}
                        />
                        <FormFeedback name='customerSource' />
                        <Field name='customerSource'>
                          {({ field }) =>
                            field.value === 'KH_OTH' ? (
                              <div className='pt-2'>
                                <Input
                                  type='textarea'
                                  name='customerSourceDetail'
                                  readOnly={!isHrmEditableExtra30days}
                                />
                                <FormFeedback name='customerSourceDetail' />
                              </div>
                            ) : null
                          }
                        </Field>
                      </FormGroup>
                    </Col>
                    <Col md='4'>
                      <FormGroup>
                        <SelectSegCus
                          name='customerSourceTwo'
                          options={options?.customerSourceTwo}
                          readOnly={!isHrmEditableExtra30days}
                          type={2}
                        />
                        <FormFeedback name='customerSourceTwo' />
                        <Field name='customerSource'>
                          {({ field }) =>
                            field.value === 'KH_OTH' ? (
                              <div className='pt-2'>
                                <Input
                                  type='textarea'
                                  name='customerSourceDetail'
                                  readOnly={!isHrmEditableExtra30days}
                                />
                                <FormFeedback name='customerSourceDetail' />
                              </div>
                            ) : null
                          }
                        </Field>
                      </FormGroup>
                    </Col>
                    <Col md='4'>
                      <FormGroup>
                        <SelectSegCus
                          name='customerSourceThree'
                          options={options?.customerSourceThree}
                          readOnly={!isHrmEditableExtra30days}
                          type={3}
                        />
                        <FormFeedback name='customerSourceThree' />
                        <Field name='customerSourceThree'>
                          {({ field }) =>
                            field.value === 'KH_OTH' ? (
                              <div className='pt-2'>
                                <Input
                                  type='textarea'
                                  name='customerSourceDetail'
                                  readOnly={!isHrmEditableExtra30days}
                                />
                                <FormFeedback name='customerSourceDetail' />
                              </div>
                            ) : null
                          }
                        </Field>
                      </FormGroup>
                    </Col>
                  </Row>
                </FormGroup>
              </Col>
              <Col md='6'>
                <FormGroup>
                  <Label for='inMonthLoan' required>
                    Khách hàng có đang trong thời hạn 60 ngày trước và 60 ngày sau ngày giải ngân toàn bộ khoản vay
                    không?
                  </Label>
                  <RadioGroup
                    name='inMonthLoan'
                    options={options?.inMonthLoan}
                    inline
                    readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)}
                  />
                </FormGroup>
              </Col>
              <Show condition={version === 4 && bank === 'VCB'}>
                <Col md='6'>
                  <FormGroup>
                    <Label for='interest'>Quan tâm của khách hàng</Label>
                    <div>
                      <CheckboxGroup
                        name='interest'
                        options={options?.interest}
                        inline
                        className='col-md-5'
                        readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)}
                      />
                    </div>
                  </FormGroup>
                </Col>
              </Show>
              <Col md='6'>
                <FormGroup>
                  <Label for='productCares' required>
                    Những loại sản phẩm khách hàng có thể tham gia
                  </Label>
                  <SelectMut
                    name='productCares'
                    options={options?.productCares}
                    readOnly={!isHrmEditableExtra30days}
                  />
                  <FormFeedback name='productCares' />
                </FormGroup>
              </Col>
            </Row>
          </Show>
          <Row>
            <Show condition={version !== 2}>
              <Col md='6'>
                <FormGroup>
                  <Label for='approach'>
                    {'Khách hàng yêu cầu tư vấn thông qua'}
                  </Label>
                  <RadioGroup name='approach' options={options?.approach}
                              readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)} />
                  <Field name='approach'>
                    {({ field, form }) => (
                      <Show condition={field.value === 'OTHER'}>
                        <div className='pt-2'>
                          <Input
                            type='textarea'
                            name='approachNote'
                            readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)}
                            onUnmount={() => form.setFieldValue('approachNote', '')}
                          />
                          <FormFeedback name='approachNote' />
                        </div>
                      </Show>
                    )}
                  </Field>
                  <FormFeedback name='approach' />
                </FormGroup>
              </Col>
            </Show>
            <Col md='6'>
              <FormGroup>
                <Label for='ownedContract' required>KH đã từng mua BH nhân thọ trước đây chưa?</Label>
                <RadioGroup
                  name='ownedContract'
                  options={options?.ownedContract}
                  readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)}
                />
                <FormFeedback name='ownedContract' />
                <Field name='ownedContract'>
                  {({ field, form }) => (
                    <Show condition={field.value === 'OTHER'}>
                      <div className='pt-2'>
                        <Input
                          type='textarea'
                          name='ownedContractDetail'
                          readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)}
                          onUnmount={() => form.setFieldValue('ownedContractDetail', null)}
                        />
                        <FormFeedback name='ownedContractDetail' />
                      </div>
                    </Show>
                  )}
                </Field>
              </FormGroup>
            </Col>
          </Row>
        </fieldset>
        <Show condition={version === 2}>
          <fieldset>
            <legend>Đối tượng ưu đãi</legend>
            <Row>
              <Col md='6'>
                <FormGroup>
                  <Select
                    name='priority'
                    options={[
                      { value: '', label: 'Không thuộc đối tượng ưu đãi' },
                      { value: 'E', label: 'CBNV' },
                      { value: 'EP', label: 'Bố mẹ đẻ của CBNV' },
                      { value: 'EMP', label: 'Bố mẹ của Vợ/Chồng CBNV' },
                      { value: 'EM', label: 'Vợ/chồng của CBNV' },
                      { value: 'EMC', label: 'Anh/chị/em ruột của vợ chồng CBNV' },
                      { value: 'EC', label: 'Anh/chị/em ruột của CBNV' },
                      { value: 'ECH', label: 'Con ruột/con nuôi CBNV' },
                    ]}
                    readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)}
                  />
                </FormGroup>
              </Col>
            </Row>
          </fieldset>
        </Show>
        <Show condition={!isNew}>
          <Row>
            <Col md='6'>
              <FormGroup>
                <Label for='lov'>FSC định danh Lead</Label>
                <Select name='lov' options={[
                  { value: '', label: 'Chọn...' },
                  { value: 'HOT', label: 'HOT' },
                  { value: 'WARM', label: 'WARM' },
                  { value: 'COLD', label: 'COLD' },
                  { value: 'DROP-OUT', label: 'DROP-OUT' },
                ]} readOnly={!(ROLE_FSC && ROLE_EDITOR)} />
                <FormFeedback name='lov' />
              </FormGroup>
            </Col>
            <Col md='6'>
              <FormGroup>
                <Label for='estimatedFyp'>FYP dự kiến</Label>
                <Input name='estimatedFyp' type='number' readOnly={!(ROLE_FSC && ROLE_EDITOR)} />
                <FormFeedback name='estimatedFyp' />
              </FormGroup>
            </Col>
          </Row>
        </Show>
        <fieldset>
          <legend>Ghi chú</legend>
          <Row>
            <Col>
              <Input type='textarea' name='note' readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)} />
            </Col>
          </Row>
          <Row>
            <Col>
              <Checkbox
                name='allowedToContact'
                value='Y'
                label={bank === 'VCB' || bank === 'AGR' ? 'Tôi xác nhận khách hàng đã chấp thuận cho phép Ngân hàng cung cấp các thông tin trên đây cho FWD và đồng ý cho phép cán bộ tư vấn bảo hiểm của FWD liên hệ với khách hàng để tư vấn chuyên sâu về các sản phẩm bảo hiểm của FWD' : 'Tôi xác nhận khách hàng đã chấp thuận cho phép Ngân hàng tiết lộ, chia sẻ các thông tin trên đây cho FWD và các tư vấn tài chính của FWD để liên hệ, tiếp cận giới thiệu các sản phẩm của FWD'}
                readOnly={values?.allowedToContact === 'Y'}
              />
              <Checkbox
                name='isConsult'
                value='Y'
                label={'Tôi xác nhận đã giới thiệu cho khách hàng các sản phẩm của FWD liên quan đến các loại sản phẩm bảo hiểm mà khách hàng đang quan tâm và các sản phẩm bảo hiểm này phù hợp với nhu cầu tài chính, nhu cầu bảo hiểm của khách hàng'}
                readOnly={values?.isConsult === 'Y'}
              />
              <Checkbox
                name='isCommit'
                value='Y'
                label={'Tôi cam kết các nội dung được tôi kê khai, xác nhận ở trên là hoàn toàn chính xác'}
                readOnly={values?.isCommit === 'Y'}
              />
              <Show condition={!isNew && values?.allowedToContact !== 'Y'}>
                <Checkbox
                  name='allowedToContact'
                  value='N'
                  label='Cán bộ ngân hàng không giới thiệu khách hàng có những thông tin trên'
                  readOnly={!(ROLE_BANK_STAFF && ROLE_EDITOR)}
                  className='text-danger font-weight-bold'
                />
              </Show>
            </Col>
          </Row>
        </fieldset>
        <Show condition={!isNew}>
          <fieldset className='mt-3'>
            <Row>
              <Show condition={version === 3}>
                <Col md='6'>
                  <FormGroup>
                    <Checkbox name='call' value='Y' label='Gọi điện thoại' readOnly={!(ROLE_EDITOR && ROLE_FSC)} />
                    <Field name='call'>
                      {({ field, form }) => (
                        <Show condition={field.value === 'Y'}>
                          <div>
                            <RadioGroup
                              name='callResult'
                              options={[
                                { value: 'S', label: 'Được' },
                                { value: 'F', label: 'Không' },
                                { value: 'W', label: 'Tiếp tục theo dõi' },
                              ]}
                              inline
                              readOnly={!(ROLE_EDITOR && ROLE_FSC)}
                              onUnmount={() => form.setFieldValue('callResult', null)}
                            />
                            <Field name='callResult'>
                              {({ field, form }) => (
                                <>
                                  <Show condition={field.value === 'S'}>
                                    <div>
                                      <RadioGroup
                                        name='callTime'
                                        options={[
                                          { value: '1', label: 'Lần 1' },
                                          { value: '2', label: 'Lần 2' },
                                          { value: '3', label: 'Lần 3 trở lên' },
                                        ]}
                                        inline
                                        readOnly={!(ROLE_EDITOR && ROLE_FSC)}
                                        onUnmount={() => form.setFieldValue('callTime', null)}
                                      />
                                    </div>
                                  </Show>
                                  <Show condition={field.value === 'W'}>
                                    <FormGroup>
                                      <Label>Ngày dự kiến tương tác</Label>
                                      <Mask
                                        name='callDate'
                                        mask='99/99/9999'
                                        placeholder='dd/MM/yyyy'
                                        readOnly={!(ROLE_EDITOR && ROLE_FSC)}
                                        onUnmount={() => form.setFieldValue('callDate', null)}
                                      />
                                    </FormGroup>
                                  </Show>
                                </>
                              )}
                            </Field>
                          </div>
                        </Show>
                      )}
                    </Field>
                  </FormGroup>
                </Col>
                <Col md='6'>
                  <FormGroup>
                    <Checkbox name='meet' value='Y' label='Gặp trực tiếp' readOnly={!(ROLE_EDITOR && ROLE_FSC)} />
                    <Field name='meet'>
                      {({ field, form }) => (
                        <Show condition={field.value === 'Y'}>
                          <div>
                            <RadioGroup
                              name='meetResult'
                              options={[
                                { value: 'S', label: 'Được' },
                                { value: 'F', label: 'Không' },
                                { value: 'W', label: 'Tiếp tục theo dõi' },
                              ]}
                              inline
                              readOnly={!(ROLE_EDITOR && ROLE_FSC)}
                              onUnmount={() => form.setFieldValue('meetResult', null)}
                            />
                            <Field name='meetResult'>
                              {({ field, form }) => (
                                <>
                                  <Show condition={field.value === 'S'}>
                                    <div>
                                      <RadioGroup
                                        name='meetTime'
                                        options={[
                                          { value: '1', label: 'Lần 1' },
                                          { value: '2', label: 'Lần 2' },
                                          { value: '3', label: 'Lần 3 trở lên' },
                                        ]}
                                        inline
                                        readOnly={!(ROLE_EDITOR && ROLE_FSC)}
                                        onUnmount={() => form.setFieldValue('meetTime', null)}
                                      />
                                    </div>
                                  </Show>
                                  <Show condition={field.value === 'W'}>
                                    <FormGroup>
                                      <Label>Ngày dự kiến tương tác</Label>
                                      <Mask
                                        name='meetDate'
                                        mask='99/99/9999'
                                        placeholder='dd/MM/yyyy'
                                        readOnly={!(ROLE_EDITOR && ROLE_FSC)}
                                        onUnmount={() => form.setFieldValue('meetDate', null)}
                                      />
                                    </FormGroup>
                                  </Show>
                                </>
                              )}
                            </Field>
                          </div>
                        </Show>
                      )}
                    </Field>
                  </FormGroup>
                </Col>
                <Col md='6'>
                  <FormGroup>
                    <Checkbox name='mail' value='Y' label='Gửi email' readOnly={!(ROLE_EDITOR && ROLE_FSC)} />
                    <Field name='mail'>
                      {({ field, form }) => (
                        <Show condition={field.value === 'Y'}>
                          <div>
                            <RadioGroup
                              name='mailResult'
                              options={[
                                { value: 'S', label: 'Được' },
                                { value: 'F', label: 'Không' },
                                { value: 'W', label: 'Tiếp tục theo dõi' },
                              ]}
                              inline
                              readOnly={!(ROLE_EDITOR && ROLE_FSC)}
                              onUnmount={() => form.setFieldValue('mailResult', null)}
                            />
                          </div>
                        </Show>
                      )}
                    </Field>
                  </FormGroup>
                </Col>
                <Col md='6'>
                  <FormGroup>
                    <Checkbox name='other' value='Y' label='Khác' readOnly={!(ROLE_EDITOR && ROLE_FSC)} />
                    <Field name='other'>
                      {({ field, form }) => (
                        <Show condition={field.value === 'Y'}>
                          <Input
                            name='otherNote'
                            type='textarea'
                            readOnly={!(ROLE_EDITOR && ROLE_FSC)}
                            onUnmount={() => form.setFieldValue('otherNote', null)}
                          />
                        </Show>
                      )}
                    </Field>
                  </FormGroup>
                </Col>
                <Col md='6'>
                  <FormGroup>
                    <Label>Tư vấn sâu</Label>
                    <div>
                      <RadioGroup
                        name='present'
                        options={[
                          { value: 'S', label: 'Được' },
                          { value: 'F', label: 'Không' },
                        ]}
                        inline
                        readOnly={!(ROLE_EDITOR && ROLE_FSC)}
                      />
                    </div>
                  </FormGroup>
                </Col>
              </Show>
              <Col md='6'>
                <Show condition={version === 3}>
                  <FormGroup>
                    <Label>Hợp đồng</Label>
                    <div>
                      <RadioGroup
                        name='contract'
                        options={[
                          { value: 'S', label: 'Được' },
                          { value: 'F', label: 'Không' },
                          { value: 'C', label: 'Đang cân nhắc' },
                        ]}
                        inline
                        readOnly={!(ROLE_EDITOR && ROLE_FSC)}
                      />
                    </div>
                    <Field name='contract'>
                      {({ field, form }) => (
                        <>
                          <Show condition={field.value === 'C'}>
                            <FormGroup>
                              <Label>Ngày hẹn chốt hợp đồng</Label>
                              <Mask
                                name='considerDeadline'
                                mask='99/99/9999'
                                placeholder='dd/MM/yyyy'
                                onUnmount={() => form.setFieldValue('considerDeadline', null)}
                              />
                            </FormGroup>
                          </Show>
                          <Show condition={field.value === 'S'}>
                            <FormGroup>
                              <Label for='poNo'>Số hợp đồng</Label>
                              <Input type='textarea' name='poNo' readOnly={!(ROLE_FSC && ROLE_EDITOR)} />
                              <FormFeedback name='poNo' />
                            </FormGroup>
                          </Show>
                        </>
                      )}
                    </Field>
                  </FormGroup>
                </Show>
                <Show condition={version >= 2}>

                  <FormGroup>
                    <Label for='poNo'>Số hợp đồng</Label>
                    <Input type='textarea' name='poNo' readOnly={!(ROLE_FSC && ROLE_EDITOR)} />
                    <FormFeedback name='poNo' />
                  </FormGroup>
                </Show>
              </Col>
            </Row>
          </fieldset>
        </Show>
        <div className='pt-3'>
          <Show condition={ROLE_EDITOR || isHrmEditable}>
            <Show if={isNew}>
              <SubmitWithCheckBtn />
            </Show>
            <Show if={!isNew}>
              <Button type='submit' color='primary'>
                Cập nhật
              </Button>
            </Show>
          </Show>
        </div>
      </Form>
    </div>
  );
};

export default LeadForm;